<template>
    <div class="booth-login-page">
        <!-- Background Gradient Section -->
        <div class="login-background">
            <div class="gradient-overlay"></div>
            <div class="floating-shapes">
                <div class="shape shape-1"></div>
                <div class="shape shape-2"></div>
                <div class="shape shape-3"></div>
            </div>
        </div>

        <!-- Main Login Container -->
        <div class="login-container d-flex">
            <div class="container-fluid h-100">
                <div class="row h-100 align-items-center justify-content-center">
                    <!-- Login Form -->
                    <div class="col-lg-6 col-md-8 col-sm-10">
                        <div class="login-card">
                            <!-- Header -->
                            <div class="login-header">
                                <div class="header-icon">
                                    <User size="32" />
                                </div>
                                <h2>ĐĂNG NHẬP</h2>
                                <p>Quán lý lấy số thứ tự</p>
                            </div>

                            <!-- Login Form -->
                            <form @submit.prevent="handleLogin" class="login-form">
                                <!-- Booth/User ID Input -->
                                <div class="form-group mb-4">
                                    <label class="form-label">Mã Quầy / ID Nhân Viên</label>
                                    <div class="input-group input-wrapper">
                                        <span class="input-group-text">
                                            <Ticket size="18" />
                                        </span>
                                        <input 
                                            v-model="boothId" 
                                            type="text" 
                                            class="form-control" 
                                            placeholder="Nhập mã quầy..."
                                        >
                                    </div>
                                </div>

                                <!-- Password Input -->
                                <div class="form-group mb-4">
                                    <label class="form-label">Mật Khẩu</label>
                                    <div class="input-group input-wrapper">
                                        <span class="input-group-text">
                                            <Lock size="18" />
                                        </span>
                                        <input 
                                            v-model="password" 
                                            :type="showPassword ? 'text' : 'password'" 
                                            class="form-control" 
                                            placeholder="Nhập mật khẩu..."
                                        >
                                        <button 
                                            type="button" 
                                            class="btn btn-toggle-password"
                                            @click="showPassword = !showPassword"
                                        >
                                            <Eye v-if="!showPassword" size="18" />
                                            <EyeOff v-else size="18" />
                                        </button>
                                    </div>
                                </div>

                                <!-- Remember Me & Forgot Password -->
                                <div class="d-flex justify-content-between align-items-center mb-4">
                                    <div class="form-check">
                                        <input 
                                            v-model="rememberMe" 
                                            class="form-check-input" 
                                            type="checkbox" 
                                            id="rememberMe"
                                        >
                                        <label class="form-check-label" for="rememberMe">
                                            Ghi nhớ tôi
                                        </label>
                                    </div>
                                    <a href="#" class="forgot-password-link">Quên mật khẩu?</a>
                                </div>

                                <!-- Login Button -->
                                <button 
                                    type="submit" 
                                    class="btn btn-login w-100 mb-3"
                                    :disabled="isLoading"
                                >
                                    <span v-if="!isLoading" class="d-flex align-items-center justify-content-center gap-2">
                                        <LogIn size="18" />
                                        Đăng Nhập
                                    </span>
                                    <span v-else class="d-flex align-items-center justify-content-center gap-2">
                                        <div class="spinner-border spinner-border-sm" role="status">
                                            <span class="visually-hidden">Loading...</span>
                                        </div>
                                        Đang xử lý...
                                    </span>
                                </button>

                                <!-- Error Message -->
                                <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
                                    <div class="d-flex gap-2">
                                        <AlertCircle size="18" class="flex-shrink-0" />
                                        <span>{{ errorMessage }}</span>
                                    </div>
                                    <button type="button" class="btn-close" @click="errorMessage = ''"></button>
                                </div>

                                <!-- Success Message -->
                                <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
                                    <div class="d-flex gap-2">
                                        <CheckCircle2 size="18" class="flex-shrink-0" />
                                        <span>{{ successMessage }}</span>
                                    </div>
                                    <button type="button" class="btn-close" @click="successMessage = ''"></button>
                                </div>
                            </form>

                            <!-- Divider -->
                            <div class="divider my-4">
                                <span>hoặc</span>
                            </div>

                            <!-- Support Section -->
                            <div class="support-section">
                                <p class="text-center text-muted mb-3">Cần trợ giúp?</p>
                                <button class="btn btn-support w-100">
                                    <HelpCircle size="18" />
                                    <span>Liên hệ Hỗ Trợ Kỹ Thuật</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Actions } from "@/store/enums/StoreEnums";
import moment from "moment";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { hideModal } from "@/core/helpers/dom";
import { ElMessage } from "element-plus";
import { 
    User, 
    Lock, 
    Eye, 
    EyeOff, 
    Ticket, 
    LogIn, 
    CheckCircle2, 
    Clock, 
    ShieldCheck,
    AlertCircle,
    HelpCircle
} from "lucide-vue-next";
import FingerprintJS from '@fingerprintjs/fingerprintjs'

export default {
    name: 'BoothLogin',
    components: {
        User,
        Lock,
        Eye,
        EyeOff,
        Ticket,
        LogIn,
        CheckCircle2,
        Clock,
        ShieldCheck,
        AlertCircle,
        HelpCircle
    },
    data() {
        return {
            router: useRouter(),
            store: useStore(),
            boothId: "",
            password: "",
            showPassword: false,
            rememberMe: false,
            isLoading: false,
            errorMessage: "",
            successMessage: ""
        }
    },
    methods: {
        generateUUID() {
            const timestamp = Date.now().toString(36)
            const random = Math.random().toString(36).substring(2, 10)

            return `${timestamp}-${random}-${Math.random()
                .toString(36)
                .substring(2, 10)}`
        },
        async handleLogin() {
            try {
                if (!this.boothId || !this.password) {
                    this.errorMessage = "Vui lòng nhập mã quầy và mật khẩu";
                    return;
                }

                this.isLoading = true;
                this.errorMessage = "";
                this.successMessage = "";

                const device_id = this.generateUUID()
                // const device_id = await crypto.randomUUID()
                // const fp = await FingerprintJS.load()
                // const result = await fp.get()
                // const visitor_id = result.visitorId

                const data = {
                    userName: this.boothId,
                    password: this.password,
                    captcha: device_id
                }
                const userInfo = await this.store.dispatch(Actions.LOGIN_BY_BOOTH, data)
                if(userInfo.code == 200 && userInfo.data) {
                    this.successMessage = "Đăng nhập thành công!";
                    await localStorage.setItem("booth-info-device-id", device_id)
                    setTimeout(() => {
                        this.router.push("boothInfo")
                    }, 500)
                }
                else {
                    this.isLoading = false;
                    this.errorMessage = userInfo.msg
                }
            }
            catch(ex) {
                console.log(ex);
            }
        }
    },
    mounted() {
        // Load remembered booth ID if available
        const savedBoothId = localStorage.getItem("boothId");
        if (savedBoothId) {
            this.boothId = savedBoothId;
            this.rememberMe = true;
        }
    }
};
</script>

<style lang="scss" scoped>
// Color scheme from gradient
$primary-blue: #1b84ff;
$primary-red: #f1416c;
$gradient: linear-gradient(120deg, $primary-blue 0%, $primary-red 100%);
$light-bg: #f5f7fa;
$white: #ffffff;
$text-primary: #2c3e50;
$text-secondary: #7f8c8d;

.booth-login-page {
    position: relative;
    width: 100%;
    min-height: 100vh;
    overflow: hidden;

    // Background Gradient Section
    .login-background {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 0;

        .gradient-overlay {
            position: absolute;
            width: 100%;
            height: 100%;
            background: $gradient;
            opacity: 0.95;
        }

        // Floating shapes animation
        .floating-shapes {
            position: absolute;
            width: 100%;
            height: 100%;
            overflow: hidden;

            .shape {
                position: absolute;
                border-radius: 50%;
                opacity: 0.1;
                background: $white;
                animation: float 6s ease-in-out infinite;

                &.shape-1 {
                    width: 300px;
                    height: 300px;
                    top: 10%;
                    left: 10%;
                    animation-delay: 0s;
                }

                &.shape-2 {
                    width: 200px;
                    height: 200px;
                    bottom: 20%;
                    right: 15%;
                    animation-delay: 2s;
                }

                &.shape-3 {
                    width: 150px;
                    height: 150px;
                    top: 50%;
                    right: 10%;
                    animation-delay: 4s;
                }
            }
        }
    }

    // Main Login Container
    .login-container {
        position: relative;
        z-index: 10;
        width: 100%;
        height: 100vh;
        padding: 20px;

        .container-fluid {
            max-width: 1200px;
            margin: 0 auto;
        }
    }

    // Branding Section (Left Side)
    .branding-section {
        text-align: center;
        color: $white;
        animation: slideInLeft 0.6s ease-out;

        .brand-icon {
            margin-bottom: 30px;
            display: flex;
            justify-content: center;

            svg {
                filter: drop-shadow(0 4px 15px rgba(0, 0, 0, 0.2));
                animation: pulse 2s ease-in-out infinite;
            }
        }

        .brand-title {
            font-size: 2.5rem;
            font-weight: 700;
            margin-bottom: 10px;
            text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
        }

        .brand-subtitle {
            font-size: 1.1rem;
            color: rgba($white, 0.9);
            margin-bottom: 30px;
        }

        .brand-features {
            text-align: left;
            display: flex;
            flex-direction: column;
            gap: 20px;

            .feature-item {
                display: flex;
                align-items: center;
                gap: 15px;
                opacity: 0.95;
                transition: all 0.3s ease;

                &:hover {
                    opacity: 1;
                    transform: translateX(5px);
                }

                .icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                span {
                    font-size: 0.95rem;
                }
            }
        }
    }

    // Login Card (Right Side)
    .login-card {
        background: $white;
        border-radius: 15px;
        padding: 50px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        animation: slideInRight 0.6s ease-out;
        backdrop-filter: blur(10px);

        @media (max-width: 768px) {
            padding: 30px;
            border-radius: 12px;
        }

        .login-header {
            text-align: center;
            margin-bottom: 40px;

            .header-icon {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 60px;
                height: 60px;
                background: $gradient;
                color: $white;
                border-radius: 50%;
                margin-bottom: 15px;
                box-shadow: 0 4px 15px rgba($primary-blue, 0.3);
                animation: bounceIn 0.6s ease-out;
            }

            h2 {
                font-size: 1.8rem;
                font-weight: 700;
                color: $text-primary;
                margin-bottom: 5px;
            }

            p {
                color: $text-secondary;
                font-size: 1.15rem;
            }
        }

        // Form Styling
        .login-form {
            .form-group {
                .form-label {
                    display: block;
                    font-weight: 600;
                    color: $text-primary;
                    margin-bottom: 8px;
                    font-size: 0.9rem;
                }

                .input-wrapper {
                    position: relative;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    transition: all 0.3s ease;

                    &:focus-within {
                        border-color: $primary-blue;
                        box-shadow: 0 0 0 3px rgba($primary-blue, 0.1);
                    }

                    .input-group-text {
                        background: transparent;
                        border: none;
                        color: $text-secondary;
                        padding: 12px 15px;
                        font-size: 1rem;
                    }

                    .form-control {
                        border: none;
                        padding: 12px 0;
                        font-size: 1rem;
                        color: $text-primary;

                        &::placeholder {
                            color: #bdc3c7;
                        }

                        &:focus {
                            outline: none;
                            box-shadow: none;
                        }
                    }

                    .btn-toggle-password {
                        border: none;
                        background: transparent;
                        color: $text-secondary;
                        padding: 12px 15px;
                        cursor: pointer;
                        transition: color 0.3s ease;

                        &:hover {
                            color: $primary-blue;
                        }
                    }
                }
            }

            .form-check {
                margin: 0;

                .form-check-input {
                    width: 18px;
                    height: 18px;
                    border: 2px solid #ddd;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.3s ease;

                    &:checked {
                        background: $gradient;
                        border-color: $primary-blue;
                    }

                    &:focus {
                        box-shadow: 0 0 0 3px rgba($primary-blue, 0.1);
                    }
                }

                .form-check-label {
                    margin-left: 8px;
                    cursor: pointer;
                    color: $text-secondary;
                    font-weight: 500;
                    user-select: none;
                }
            }

            .forgot-password-link {
                color: $primary-blue;
                text-decoration: none;
                font-weight: 600;
                transition: all 0.3s ease;

                &:hover {
                    color: $primary-red;
                    text-decoration: underline;
                }
            }

            // Login Button
            .btn-login {
                background: $gradient;
                color: $white;
                border: none;
                padding: 12px 20px;
                font-weight: 600;
                border-radius: 8px;
                font-size: 1rem;
                cursor: pointer;
                transition: all 0.3s ease;
                box-shadow: 0 4px 15px rgba($primary-blue, 0.3);

                &:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba($primary-blue, 0.4);
                }

                &:active:not(:disabled) {
                    transform: translateY(0);
                }

                &:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .spinner-border-sm {
                    width: 16px;
                    height: 16px;
                    border-width: 2px;
                }
            }

            // Alert Styling
            .alert {
                border: none;
                border-radius: 8px;
                padding: 12px 15px;
                animation: slideDown 0.3s ease-out;

                &.alert-danger {
                    background-color: #fee;
                    color: #c33;

                    .btn-close {
                        color: #c33;
                    }
                }

                &.alert-success {
                    background-color: #efe;
                    color: #3c3;

                    .btn-close {
                        color: #3c3;
                    }
                }
            }
        }

        // Divider
        .divider {
            text-align: center;
            position: relative;
            color: $text-secondary;
            font-size: 0.9rem;

            &::before,
            &::after {
                content: "";
                position: absolute;
                top: 50%;
                width: 45%;
                height: 1px;
                background: #e0e0e0;
            }

            &::before {
                left: 0;
            }

            &::after {
                right: 0;
            }

            span {
                position: relative;
                background: $white;
                padding: 0 10px;
            }
        }

        // Support Section
        .support-section {
            .btn-support {
                background: transparent;
                color: $primary-blue;
                border: 2px solid $primary-blue;
                padding: 10px 20px;
                font-weight: 600;
                border-radius: 8px;
                cursor: pointer;
                transition: all 0.3s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;

                &:hover {
                    background: rgba($primary-blue, 0.05);
                    border-color: $primary-red;
                    color: $primary-red;
                    transform: translateY(-2px);
                }
            }
        }

        // Footer
        .login-footer {
            text-align: center;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e0e0e0;
            font-size: 0.85rem;
        }
    }
}

// Animations
@keyframes slideInLeft {
    from {
        opacity: 0;
        transform: translateX(-50px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes slideInRight {
    from {
        opacity: 0;
        transform: translateX(50px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes bounceIn {
    0% {
        opacity: 0;
        transform: scale(0.3);
    }
    50% {
        opacity: 1;
    }
    100% {
        transform: scale(1);
    }
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}

@keyframes float {
    0%, 100% {
        transform: translateY(0px);
    }
    50% {
        transform: translateY(30px);
    }
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// Responsive Design
@media (max-width: 992px) {
    .booth-login-page {
        .login-card {
            padding: 30px;
        }
    }
}

@media (max-width: 768px) {
    .booth-login-page {
        .login-container {
            padding: 15px;
        }

        .login-card {
            padding: 25px;

            .login-header {
                margin-bottom: 30px;

                h2 {
                    font-size: 1.5rem;
                }
            }

            .login-form {
                .form-group {
                    margin-bottom: 16px !important;
                }
            }
        }
    }
}
</style>